/**
 * Live mode's edges on the web (docs/plans/os-voice-conversation.md, C16,
 * step 3.12; boards WX06 to WX12, WE11, WE14 to WE16), as pure rules, so the
 * hook, the composer and the thread only draw them.
 *
 * - **The look a call shows** (`webLookOf`): the shared `liveStateOf` over
 *   what the browser knows (the server's state, the connection, Mute, the
 *   voice playing, the microphone's permission, the first-run mute tip).
 * - **Earcons** (`earconStep`): the state a look enters plays its cue once,
 *   outside the voice's queue; the rising chime only the first time a call
 *   listens (a re-attach, Listening again after an answer, or a call that
 *   carries live mode on after a setup's Create plays none);
 *   the switch to half duplex makes no sound; the falling chime is the
 *   ending's (`LIVE_END_SOUND`). The web never vibrates.
 * - **What the thread says when live mode could not run, or another device
 *   has it** (`liveNoteOf`): the browser blocked the microphone (an OS
 *   message with the three steps, Try again and Type instead), no
 *   microphone, a microphone that would not start, OS could not answer (Ask
 *   again), and live mode running on another device (Continue live here).
 * - **The half-duplex note** (`halfDuplexNoteOf`): "Voice can't interrupt
 *   while OS talks. Earphones fix this." once per browser, beside Interrupt.
 * - **Talk live with the microphone blocked** (`talkLiveBlocked`): live mode
 *   does not start; the thread says why instead.
 */
import {
  t,
  liveStateOf,
  type LiveConnection,
  type LiveDuplexMode,
  type LiveLook,
  type LiveServerState,
  type LiveSound,
  type LiveStateName,
  type SaveWordsLanguage,
} from '@alluzo/shared';

import type { LiveEnding } from './live-mode';

export type WebLookInput = {
  /** The connection as the hook knows it. */
  readonly connection: LiveConnection;
  /** The call runs the version-2 wire. */
  readonly v2: boolean;
  /** A legacy call's own turn, read as the server's state for the same look. */
  readonly legacyTurn: 'listening' | 'working' | 'speaking';
  readonly server: LiveServerState | null;
  readonly duplex: LiveDuplexMode | null;
  readonly muted: boolean;
  readonly playing: boolean;
  readonly endsInMs?: number;
  /** The browser refused the microphone. */
  readonly micDenied: boolean;
  /** This browser has not seen the "Tap to mute" tip go. */
  readonly muteTip: boolean;
  /** The self-echo detector dropped a full-duplex call to half (step 4.3). */
  readonly echo?: boolean;
  /** Earphones brought it back: their note shows. */
  readonly earphonesNote?: boolean;
  /** OS plays ducked under the person's voice, and the room reads loud (step 4.4). */
  readonly ducked?: boolean;
  readonly loud?: boolean;
  /** Another app or the system holds the audio (`webAudioStep`): Paused, with Resume. */
  readonly interrupted?: boolean;
  /** The language the person speaks to OS: Needs a click names the save words in it (7 Oct, item 4). */
  readonly saveLanguage?: SaveWordsLanguage;
  /** The server's `name_hint` came a moment ago: Listening says "say “OS” to ask" (7 Oct evening, item 4). */
  readonly nameHint?: boolean;
  /** The server's last `state` said the conversation window is open: Listening rings the orb (option B, step 2). */
  readonly windowOpen?: boolean;
  /** The server's `unsure` came a moment ago: the "Was that for me?" chip (option B, step 5). */
  readonly claim?: boolean;
};

/** The look a call up shows on the web. A legacy call has no ask chip: a click on the orb talks. */
export const webLookOf = (input: WebLookInput): LiveLook =>
  liveStateOf({
    platform: 'web',
    connection: input.connection,
    server: input.v2 ? input.server : input.legacyTurn,
    duplex: input.v2 ? input.duplex : 'legacy',
    muted: input.muted,
    playing: input.playing,
    ducked: input.ducked === true,
    mic: input.micDenied ? 'denied' : 'granted',
    echo: input.echo === true,
    quiet: false,
    loud: input.loud === true,
    interrupted: input.interrupted === true,
    muteTip: input.muteTip,
    ...(input.saveLanguage === undefined ? {} : { saveLanguage: input.saveLanguage }),
    ...(input.v2 && input.nameHint === true ? { nameHint: true } : {}),
    ...(input.v2 && input.windowOpen === true ? { windowOpen: true } : {}),
    ...(input.v2 && input.claim === true ? { claim: true } : {}),
    ...(input.earphonesNote === true ? { earphonesNote: true } : {}),
    ...(input.v2 && input.endsInMs !== undefined ? { endsInMs: input.endsInMs } : {}),
  });

/** The falling chime, played as live mode ends (Stop live, Close, and every ending of its own). */
export const LIVE_END_SOUND: LiveSound = 'chime_fall';

/**
 * The earcon a new look plays, if any: its state's cue, once, on entering the
 * state. The rising chime is the call's start only (`risen` says it played).
 */
export const earconStep = ({
  was,
  look,
  risen,
  carried = false,
}: {
  readonly was: LiveStateName | null;
  readonly look: LiveLook;
  readonly risen: boolean;
  /** Live mode carries on after a setup's Create (`after: 'create'`): it never starts, so it never rises. */
  readonly carried?: boolean;
}): { readonly sound: LiveSound | null; readonly risen: boolean } => {
  if (was === look.state) return { sound: null, risen };
  const sound = look.cues.sound ?? null;
  if (sound === 'chime_rise') return risen || carried ? { sound: null, risen: true } : { sound, risen: true };
  return { sound, risen };
};

/** What the thread says when live mode could not run, or ran into an edge. */
export type LiveNoteKind = 'mic_blocked' | 'no_mic' | 'mic_unsupported' | 'mic_failed' | 'couldnt_answer' | 'elsewhere';

/** Why live mode may not start at all: the browser refused the microphone, or this page cannot reach one. */
export type LiveMicBlock = 'mic_blocked' | 'mic_unsupported';

/**
 * Live mode's gate, before any call: go, or the note that says why not. A
 * page with no `getUserMedia` (an insecure page, an old browser) can never
 * reach a microphone, whatever the person allows; a refusal stands until they
 * change the site's setting. Anything else (the browser will ask, it said
 * yes, it cannot say) goes, and the ask itself answers.
 */
export const liveMicGateOf = ({
  permission,
  mediaDevices,
  inApp = false,
}: {
  readonly permission: string;
  readonly mediaDevices: boolean;
  /** An app's own browser (`inAppBrowserOf`): it cannot use the mic, whatever it says it has. */
  readonly inApp?: boolean;
}): 'go' | LiveMicBlock => {
  if (!mediaDevices || inApp) return 'mic_unsupported';
  return talkLiveBlocked(permission) ? 'mic_blocked' : 'go';
};

/**
 * A browser inside another app (a chat or social app's own web view, or an
 * Android WebView), which cannot use the microphone for a page: `in_app`, or
 * null for a browser of its own. Read from the user agent; never names the
 * app (step 7.7 of docs/plans/onboarding-harness/phase-07.md).
 */
export const inAppBrowserOf = (userAgent: string): 'in_app' | null => (/FBAN|FBAV|Instagram|WhatsApp|Line\/|Snapchat|; wv\)/.test(userAgent) ? 'in_app' : null);

/** The browsers whose way to allow the microphone differs: a Chromium browser, Safari, Firefox, or one we cannot tell. */
export type MicBrowser = 'chromium' | 'safari' | 'firefox' | 'other';

/** Which browser a user agent is, as far as allowing the microphone goes. */
export const micBrowserOf = (userAgent: string): MicBrowser => {
  // iOS browsers other than Safari keep their own settings: said generically.
  if (/CriOS|FxiOS|EdgiOS/.test(userAgent)) return 'other';
  if (/Firefox\//.test(userAgent)) return 'firefox';
  if (/Edg\/|OPR\/|Chrom(e|ium)\//.test(userAgent)) return 'chromium';
  if (/Safari\//.test(userAgent) && /Version\//.test(userAgent)) return 'safari';
  return 'other';
};

/**
 * The steps to allow the microphone again in this browser, ending in "Come
 * back and click Try again.": the browser's own way where we know it, the
 * generic lock and switch where we do not.
 */
export const micAllowStepsOf = (browser: MicBrowser): readonly string[] => {
  const retry = t('agent.live.mic_blocked.step_retry');
  switch (browser) {
    case 'chromium':
      return [t('agent.web.mic_allow.chromium'), retry];
    case 'safari':
      return [t('agent.web.mic_allow.safari'), retry];
    case 'firefox':
      return [t('agent.web.mic_allow.firefox'), retry];
    case 'other':
      return [t('agent.live.mic_blocked.step_lock'), t('agent.live.mic_blocked.step_allow'), retry];
  }
};

/** Whether a note's Try again can work: not where the page cannot reach a microphone at all. */
export const noteRetries = (kind: LiveNoteKind): boolean => kind === 'mic_blocked' || kind === 'no_mic' || kind === 'mic_failed';

export const liveNoteOf = ({
  ending,
  opened,
  elsewhere,
}: {
  /** Why the last call ended, or null with none. */
  readonly ending: LiveEnding | null;
  /** The call opened before it ended (it leaves a divider): another device's refusal comes before it ever opens. */
  readonly opened: boolean;
  /** Another device runs the person's live call (the server said `elsewhere`). */
  readonly elsewhere: boolean;
}): LiveNoteKind | null => {
  if (elsewhere && !opened) return 'elsewhere';
  switch (ending) {
    case 'mic_off':
      return 'mic_blocked';
    case 'no_mic':
      return 'no_mic';
    case 'mic_failed':
      return 'mic_failed';
    case 'failed':
      return 'couldnt_answer';
    default:
      return null;
  }
};

/** Talk live with the microphone blocked: live mode does not start, and the thread says why. */
export const talkLiveBlocked = (permission: string): boolean => permission === 'denied';

/** The half-duplex note beside Interrupt, once per browser. */
export const halfDuplexNoteOf = ({ look, seen }: { readonly look: LiveLook | null; readonly seen: boolean }): boolean => !seen && look?.askAction === 'interrupt';
